/* Pure helpers behind the History tab: the training heatmap (one column per
 * Mon-Sun week), the missed-day tally under it and the session record of a
 * date. The plan day of every date comes from logic/schedule planDayFor, so a
 * stamped session keeps its label when the cycle is shifted later. Nothing
 * here reads the clock: the tab passes todayKey() in. */

import { mondayOf, planDayFor } from '../logic/schedule';
import { dayKey, dayNum } from '../stats/statsData';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function isObj(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function own(obj, key) {
  return isObj(obj) && Object.prototype.hasOwnProperty.call(obj, key) ? obj[key] : undefined;
}

/** Sets ticked done in one day's log (what isTrainingDate counts). */
export function doneSetCount(dayLog) {
  if (!isObj(dayLog)) return 0;
  return Object.values(dayLog).reduce(
    (n, sets) => n + (Array.isArray(sets) ? sets.filter(s => isObj(s) && s.done).length : 0), 0
  );
}

/** Heatmap shade by done sets: 1-9, 10-17, 18+ (0 = untrained). */
export function shadeFor(sets) {
  if (sets >= 18) return 3;
  if (sets >= 10) return 2;
  return sets > 0 ? 1 : 0;
}

/** First date with a done set, or null. Before it nothing counts as missed. */
export function firstTrainingDate(logs) {
  let first = null;
  Object.keys(isObj(logs) ? logs : {}).forEach(date => {
    if (dayNum(date) === null || (first !== null && date >= first)) return;
    if (doneSetCount(logs[date]) > 0) first = date;
  });
  return first;
}

/** Latest date up to `today` with a done set, or null. */
export function latestSessionDate(logs, today) {
  let latest = null;
  Object.keys(isObj(logs) ? logs : {}).forEach(date => {
    if (dayNum(date) === null || date > today || (latest !== null && date <= latest)) return;
    if (doneSetCount(logs[date]) > 0) latest = date;
  });
  return latest;
}

/**
 * One date on the heatmap.
 * kind: 'future' (after today), 'trained' (a session on a planned training
 * day), 'extra' (a session on a planned rest day), 'rest' (planned rest),
 * 'pending' (today, planned, nothing done yet), 'missed' (a planned training
 * day without a session) or 'none' (before tracking started, or no plan).
 * @param {object} state reads logs, plan, sessions, cycleAnchor
 * @param {string} date YYYY-MM-DD
 * @param {{today:string, firstDate:string|null, fallbackAnchor?:string}} ctx
 * @returns {{date, kind, sets, shade, planDay:string|null, source:'stamp'|'cycle'|null, isToday:boolean}}
 */
export function dayCell(state, date, { today, firstDate, fallbackAnchor }) {
  const n = dayNum(date);
  const t = dayNum(today);
  const p = planDayFor(state, date, fallbackAnchor || mondayOf(today));
  const day = p && isObj(p.day) ? p.day : null;
  const sets = doneSetCount(own(state?.logs, date));
  const cell = {
    date,
    kind: 'none',
    sets,
    shade: shadeFor(sets),
    planDay: day && typeof day.name === 'string' ? day.name : null,
    source: p ? p.source : null,
    isToday: n === t,
  };
  if (n === null || t === null) return cell;
  if (n > t) cell.kind = 'future';
  else if (sets > 0) cell.kind = day && day.isRest ? 'extra' : 'trained';
  else if (!day || !firstDate || date < firstDate) cell.kind = 'none';
  else if (day.isRest) cell.kind = 'rest';
  else if (n === t) cell.kind = 'pending';
  else cell.kind = 'missed';
  return cell;
}

/**
 * Heatmap columns: `weeks` Mon-Sun weeks, the current week last. A column is
 * labelled with a month when it holds that month's 1st; the first column
 * gets its own month unless a label follows within 2 columns (they'd overlap).
 * @param {object} state reads logs, plan, sessions, cycleAnchor
 * @param {string} today YYYY-MM-DD workout day
 * @param {{weeks?:number}} [opts]
 * @returns {{columns:Array<{monday:string, month:string|null, days:Array}>,
 *   start:string|null, end:string|null, firstDate:string|null, hasOlder:boolean}}
 *   hasOlder: sessions exist before the first column
 */
export function heatmapColumns(state, today, { weeks = 24 } = {}) {
  const monday = mondayOf(today);
  const firstDate = firstTrainingDate(state?.logs);
  if (!monday || weeks < 1) return { columns: [], start: null, end: null, firstDate, hasOlder: false };
  const last = dayNum(monday);
  const ctx = { today, firstDate, fallbackAnchor: monday };
  const columns = [];
  for (let w = weeks - 1; w >= 0; w -= 1) {
    const m = last - 7 * w;
    const days = [];
    let month = null;
    for (let i = 0; i < 7; i += 1) {
      const key = dayKey(m + i);
      if (key.endsWith('-01')) month = MONTHS[Number(key.slice(5, 7)) - 1];
      days.push(dayCell(state, key, ctx));
    }
    columns.push({ monday: dayKey(m), month, days });
  }
  if (!columns[0].month && !columns.slice(1, 3).some(c => c.month)) {
    columns[0].month = MONTHS[Number(columns[0].monday.slice(5, 7)) - 1];
  }
  const start = columns[0].monday;
  return {
    columns,
    start,
    end: dayKey(last + 6),
    firstDate,
    hasOlder: !!firstDate && firstDate < start,
  };
}

/**
 * Misses per plan day over the heatmap, for "Legs A 3 of 12": planned counts
 * the planned training days that could have been done (since tracking
 * started, before today), missed those without a session.
 * @param {Array} columns heatmapColumns().columns
 * @returns {Array<{planDay:string, missed:number, planned:number}>} only plan
 *   days with a miss, most missed first
 */
export function missedTally(columns) {
  const by = new Map();
  (Array.isArray(columns) ? columns : []).forEach(col => col.days.forEach(c => {
    if (!c.planDay || (c.kind !== 'trained' && c.kind !== 'missed')) return;
    const row = by.get(c.planDay) || { planDay: c.planDay, missed: 0, planned: 0 };
    row.planned += 1;
    if (c.kind === 'missed') row.missed += 1;
    by.set(c.planDay, row);
  }));
  return [...by.values()].filter(r => r.missed > 0)
    .sort((a, b) => b.missed - a.missed || b.missed / b.planned - a.missed / a.planned || a.planDay.localeCompare(b.planDay));
}

/**
 * The session record (sessions[date]) and readiness check-in (recovery[date])
 * of a date, cleaned for display. The duration needs both ticks from the live
 * day; sets ticked after the fact give nonsense, so only 1-300 min shows.
 * @returns {{durationMin:number|null, rpe:number|null, note:string, override:boolean,
 *   sleepH:number|null, energy:number|null, soreness:number|null, soreAreas:string[], any:boolean}}
 */
export function sessionInfo(state, date) {
  const s = own(state?.sessions, date);
  const r = own(state?.recovery, date);
  const num = (v, lo, hi) => (typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi ? v : null);
  let durationMin = null;
  if (isObj(s) && typeof s.startedAt === 'number' && typeof s.lastSetAt === 'number') {
    const min = Math.round((s.lastSetAt - s.startedAt) / 60000);
    if (min >= 1 && min <= 300) durationMin = min;
  }
  const out = {
    durationMin,
    rpe: isObj(s) ? num(s.rpe, 1, 10) : null,
    note: isObj(s) && typeof s.note === 'string' ? s.note.trim() : '',
    override: isObj(s) && s.override === true,
    sleepH: isObj(r) ? num(r.sleepH, 0, 24) : null,
    energy: isObj(r) ? num(r.energy, 1, 5) : null,
    soreness: isObj(r) ? num(r.soreness, 1, 5) : null,
    soreAreas: isObj(r) && Array.isArray(r.soreAreas) ? r.soreAreas.filter(a => typeof a === 'string' && a) : [],
  };
  out.any = out.durationMin !== null || out.rpe !== null || !!out.note
    || out.sleepH !== null || out.energy !== null || out.soreness !== null || out.soreAreas.length > 0;
  return out;
}

/** '1h 05m' / '48 min'. */
export function fmtDuration(min) {
  if (typeof min !== 'number' || !(min > 0)) return '';
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, '0')}m`;
}

/** RIR badge text: 5 means "4+". */
export function rirLabel(rir) {
  if (typeof rir !== 'number' || !Number.isFinite(rir) || rir < 0) return null;
  return rir >= 5 ? '4+' : String(Math.round(rir));
}

/**
 * The exercises of one date for the day detail: the plan day's exercises
 * with that date's swaps and set-count overrides applied, then anything else
 * logged (extras). A rest day has no planned exercises.
 * @param {object} state reads logs, swaps, setCounts
 * @param {string} date YYYY-MM-DD
 * @param {object|null} planDay the plan entry (planDayFor().day)
 * @returns {Array<{id, name, planned:boolean, swappedFrom:string|null, sets:number, reps:string}>}
 */
export function dayExercises(state, date, planDay) {
  const log = own(state?.logs, date);
  const swaps = own(state?.swaps, date);
  const counts = own(state?.setCounts, date);
  const planned = (isObj(planDay) && !planDay.isRest && Array.isArray(planDay.exercises) ? planDay.exercises : [])
    .filter(isObj)
    .map(e => {
      const swapped = typeof own(swaps, e.id) === 'string' && swaps[e.id] && swaps[e.id] !== e.name ? swaps[e.id] : null;
      const count = own(counts, e.id);
      return {
        id: e.id,
        name: swapped || e.name,
        planned: true,
        swappedFrom: swapped ? e.name : null,
        sets: typeof count === 'number' ? count : (Number(e.sets) || 0),
        reps: e.repMin && e.repMax ? `${e.repMin}-${e.repMax}` : String(e.reps ?? ''),
      };
    });
  const names = new Set(planned.map(e => e.name));
  const extras = Object.keys(isObj(log) ? log : {}).filter(n => !names.has(n))
    .map(name => ({ id: `adhoc-${name}`, name, planned: false, swappedFrom: null, sets: 0, reps: '' }));
  return [...planned, ...extras];
}
